import {
  Asterisk,
  CalendarDays,
  ListChecks,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Settings,
  Sparkles,
  Sun,
  Video,
  X,
} from "lucide-react";
import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AtwLogo } from "@/components/AtwLogo";
import { HubAiPanel } from "@/components/HubAiPanel";
import { PaneResizer } from "@/components/PaneResizer";
import { api } from "@/lib/api";
import { openHubAi } from "@/lib/hubAssist";
import {
  enterHubSearchSection,
  hubSearchSection,
  setHubSearchQuery,
  useHubSearchQuery,
} from "@/lib/hubSearch";
import { useAuth } from "@/lib/auth";
import { useSlackCustomerClaim } from "@/lib/slackCustomer";
import { HUB_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";

/** Primary modules — desktop icon rail matches older Hub (no Hub AI / Settings in rail). */
const modules = [
  { to: "/", label: "Hub", icon: Asterisk },
  { to: "/mail", label: "Mail", icon: Mail },
  { to: "/chat", label: "Chat", icon: MessageSquare },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/meetings", label: "Meetings", icon: Video },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
];

/** Phone drawer lists every module plus Hub AI (desktop has Hub AI at the right of the bottom bar). */
const mobileModules = [...modules, { to: "/ai", label: "Hub AI", icon: Sparkles }];
/** Phone bottom bar keeps five modules + Hub AI; Tasks is in the drawer. */
const phoneBarModules = modules.filter((item) => item.to !== "/tasks");

const SIDEBAR_COLLAPSE_KEY = "atw-hub-sidebar-collapsed";

function initials(email?: string, name?: string) {
  const source = (name || email || "AT").trim();
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function searchPlaceholder(pathname: string) {
  if (pathname.startsWith("/mail")) return "Search in mail";
  if (pathname.startsWith("/calendar")) return "Search in calendar";
  if (pathname.startsWith("/meetings")) return "Search in meetings";
  if (pathname.startsWith("/tasks")) return "Search in tasks";
  if (pathname.startsWith("/chat")) return "Search in chat";
  if (pathname.startsWith("/ai")) return "Ask Hub AI";
  if (pathname === "/") return "Search Hub";
  return "Search mail";
}

function TopbarSearchInput({
  pathname,
  navigate,
  placeholder,
}: {
  pathname: string;
  navigate: ReturnType<typeof useNavigate>;
  placeholder: string;
}) {
  const section = hubSearchSection(pathname);
  const query = useHubSearchQuery(section);

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setHubSearchQuery("", section);
      event.currentTarget.blur();
      return;
    }
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    const text = query.trim();
    if (!text) return;
    if (section === "ai") {
      event.preventDefault();
      openHubAi({ prompt: text, send: true });
      setHubSearchQuery("", section);
      return;
    }
    // Hub home and Settings have no list of their own: Enter searches Mail.
    if (section === "home" || section === "other") {
      event.preventDefault();
      setHubSearchQuery(text, "mail");
      navigate("/mail");
    }
  }

  return (
    <input
      type="search"
      placeholder={placeholder}
      aria-label={placeholder}
      value={query}
      onChange={(event) => setHubSearchQuery(event.target.value, section)}
      onKeyDown={onKeyDown}
    />
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadMail, setUnreadMail] = useState(0);
  // Target older Hub: desktop rail is icon-only by default (unset → collapsed).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSE_KEY);
      if (stored === null) return true;
      return stored === "1";
    } catch {
      return true;
    }
  });
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === "dark");
  const [composeOpen, setComposeOpen] = useState(
    () => typeof document !== "undefined" && document.documentElement.dataset.compose === "1",
  );
  useSlackCustomerClaim(Boolean(user));

  useEffect(() => {
    enterHubSearchSection(hubSearchSection(pathname));
  }, [pathname]);

  const showFab =
    !composeOpen &&
    (pathname.startsWith("/mail") || pathname.startsWith("/calendar") || pathname.startsWith("/chat"));
  const fabLabel = pathname.startsWith("/calendar")
    ? "New Event"
    : pathname.startsWith("/chat")
      ? "New Chat"
      : "New Message";
  const profileInitials = initials(user?.email, user?.name || user?.displayName);
  const searchLabel = searchPlaceholder(pathname);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".profile-wrap")) return;
      setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    function onComposeOpen() {
      setComposeOpen(true);
    }
    function onComposeClose() {
      setComposeOpen(false);
    }
    window.addEventListener("atw-hub-compose-open", onComposeOpen);
    window.addEventListener("atw-hub-compose-close", onComposeClose);
    return () => {
      window.removeEventListener("atw-hub-compose-open", onComposeOpen);
      window.removeEventListener("atw-hub-compose-close", onComposeClose);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadMail(0);
      return;
    }
    let cancelled = false;
    async function refreshUnread() {
      // Tip 081: never fall back to a full `/api/mail` inbox pull — that race hung
      // Mail first paint (API paginates Gmail to 100–150 threads) while the badge refreshed.
      try {
        const home = await api<{ unreadMail?: number }>("/api/home", { timeoutMs: 8000 });
        if (!cancelled && typeof home.unreadMail === "number") {
          setUnreadMail(home.unreadMail);
        }
      } catch {
        /* leave prior badge; MailPage owns the list fetch */
      }
    }
    void refreshUnread();
    const timer = window.setInterval(() => void refreshUnread(), 20000);
    window.addEventListener("atw-hub-refresh", refreshUnread);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("atw-hub-refresh", refreshUnread);
    };
  }, [user, pathname]);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    document.documentElement.dataset.theme = next ? "dark" : "light";
    localStorage.setItem("atw-hub-theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
    document.documentElement.style.colorScheme = next ? "dark" : "light";
  }

  function toggleSidebar() {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  }

  function onFab() {
    if (pathname.startsWith("/calendar")) {
      window.dispatchEvent(new CustomEvent("atw-hub-new-event"));
      return;
    }
    if (pathname.startsWith("/chat")) {
      window.dispatchEvent(new CustomEvent("atw-hub-new-chat"));
      return;
    }
    // Open compose over the current mail view (keep open email underneath).
    window.dispatchEvent(new CustomEvent("atw-hub-new-message"));
    if (!pathname.startsWith("/mail")) {
      navigate("/mail");
    }
  }

  function renderProfileMenu() {
    if (!menuOpen) return null;
    return (
      <div className="profile-menu">
        <p className="profile-email">{user?.email}</p>
        {(pathname.startsWith("/mail") || pathname === "/") && (
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onFab();
            }}
          >
            <Mail size={16} /> New Message
          </button>
        )}
        {pathname.startsWith("/chat") && (
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onFab();
            }}
          >
            <MessageSquare size={16} /> New Chat
          </button>
        )}
        {pathname.startsWith("/calendar") && (
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onFab();
            }}
          >
            <CalendarDays size={16} /> New Event
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            navigate("/settings");
          }}
        >
          <Settings size={16} /> Settings
        </button>
        <button
          type="button"
          onClick={() => {
            toggleTheme();
          }}
        >
          {dark ? <Sun size={16} /> : <Moon size={16} />} {dark ? "Light mode" : "Dark mode"}
        </button>
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            void signOut();
          }}
        >
          <LogOut size={16} /> Sign Out
        </button>
        <p className="profile-version">{HUB_VERSION}</p>
      </div>
    );
  }

  return (
    <div className={cn("hub-shell", sidebarCollapsed && "sidebar-collapsed")}>
      <aside className="hub-sidebar desktop-only" aria-label="Modules">
        <div className="sidebar-brand">
          <AtwLogo size={sidebarCollapsed ? 32 : 40} onDark />
          <div className="sidebar-brand-text">
            <p className="brand-title">ATW Hub</p>
            <p className="brand-sub">{HUB_VERSION}</p>
          </div>
        </div>
        <nav className="sidebar-nav">
          {modules.map((item) => {
            const Icon = item.icon;
            const mailBadge = item.to === "/mail" && unreadMail > 0 ? unreadMail : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                title={item.label}
                className={({ isActive }) => cn("sidebar-link", isActive && "active")}
              >
                <span className="sidebar-icon-wrap">
                  <Icon size={18} aria-hidden />
                  {mailBadge > 0 ? (
                    <span className="nav-badge" aria-label={`${mailBadge} unread`}>
                      {mailBadge > 99 ? "99+" : mailBadge}
                    </span>
                  ) : null}
                </span>
                <span className="sidebar-link-label">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>
      {!sidebarCollapsed ? (
        <PaneResizer
          id="shell.sidebar"
          cssVar="--hub-sidebar-w"
          min={180}
          max={320}
          mainMin={640}
          label="Resize module sidebar"
        />
      ) : null}

      <div className="hub-main" data-pane-main>
        <header className={menuOpen ? "hub-topbar menu-open" : "hub-topbar"}>
          {/* Phone: hamburger + search + profile. Desktop: compact Search pill (far right). */}
          <button
            type="button"
            className="icon-btn phone-only"
            aria-label={
              pathname.startsWith("/mail")
                ? "Open Emails"
                : pathname.startsWith("/calendar")
                  ? "Open Calendars"
                  : pathname.startsWith("/chat")
                    ? "Open Chat"
                    : pathname.startsWith("/meetings")
                      ? "Open Meetings"
                      : pathname.startsWith("/settings")
                        ? "Open Settings"
                        : "Open menu"
            }
            onClick={() => {
              // Phone Mail: hamburger opens the Emails folder drawer (shots 11–14), not the module list.
              if (pathname.startsWith("/mail")) {
                window.dispatchEvent(new CustomEvent("atw-hub-open-mail-drawer"));
                return;
              }
              if (pathname.startsWith("/calendar")) {
                window.dispatchEvent(new CustomEvent("atw-hub-open-calendar-drawer"));
                return;
              }
              if (pathname.startsWith("/chat")) {
                window.dispatchEvent(new CustomEvent("atw-hub-open-chat-drawer"));
                return;
              }
              if (pathname.startsWith("/meetings")) {
                window.dispatchEvent(new CustomEvent("atw-hub-open-meetings-drawer"));
                return;
              }
              // Phone Settings: hamburger opens Settings sections (Profile, Connections, …).
              if (pathname.startsWith("/settings")) {
                window.dispatchEvent(new CustomEvent("atw-hub-open-settings-drawer"));
                return;
              }
              setDrawerOpen(true);
            }}
          >
            <Menu size={20} />
          </button>
          <div className="topbar-search phone-only">
            <TopbarSearchInput pathname={pathname} navigate={navigate} placeholder={searchLabel} />
          </div>
          <div className="topbar-spacer desktop-only" aria-hidden />
          <label className="topbar-search-pill desktop-only">
            <Search size={16} aria-hidden />
            <TopbarSearchInput pathname={pathname} navigate={navigate} placeholder={searchLabel} />
          </label>
          <div className="profile-wrap phone-only">
            <button
              type="button"
              className="avatar-btn"
              aria-label="Account menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {profileInitials}
            </button>
            {renderProfileMenu()}
          </div>
        </header>

        <main className="hub-content">{children}</main>

        {showFab ? (
          <button type="button" className="module-fab phone-only" onClick={onFab}>
            <Plus size={16} />
            {fabLabel}
          </button>
        ) : null}
      </div>

      {/* Full-viewport bottom chrome — spans under sidebar on desktop (Profile+Collapse | Hub AI). */}
      <nav className="bottom-bar" aria-label="Hub chrome">
        <div className="bottom-bar-desktop desktop-only">
          <div className="bottom-bar-left">
            <div className="profile-wrap bottom-profile-wrap">
              <button
                type="button"
                className="avatar-btn bottom-avatar"
                aria-label="Account menu"
                onClick={() => setMenuOpen((v) => !v)}
              >
                {profileInitials}
              </button>
              {renderProfileMenu()}
            </div>
            <button
              type="button"
              className="icon-btn bottom-collapse-btn"
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed ? "Expand" : "Collapse"}
              onClick={toggleSidebar}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
          <div className="bottom-bar-right">
            <button
              type="button"
              title="Ask Hub AI"
              className={cn("bottom-module bottom-hub-ai", pathname.startsWith("/ai") && "active")}
              onClick={() => (pathname.startsWith("/ai") ? undefined : openHubAi())}
            >
              <Sparkles />
              <span>Ask Hub AI</span>
            </button>
          </div>
        </div>

        <div className="bottom-bar-mobile phone-only">
          {phoneBarModules.map((item) => {
            const Icon = item.icon;
            const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
            const mailBadge = item.to === "/mail" && unreadMail > 0 ? unreadMail : 0;
            return (
              <NavLink key={item.to} to={item.to} className={cn("bottom-module", active && "active")}>
                <span className="sidebar-icon-wrap">
                  <Icon />
                  {mailBadge > 0 ? (
                    <span className="nav-badge" aria-label={`${mailBadge} unread`}>
                      {mailBadge > 99 ? "99+" : mailBadge}
                    </span>
                  ) : null}
                </span>
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          <button
            type="button"
            className={cn("bottom-module", pathname.startsWith("/ai") && "active")}
            onClick={() => (pathname.startsWith("/ai") ? undefined : openHubAi())}
          >
            <span className="sidebar-icon-wrap">
              <Sparkles />
            </span>
            <span>Hub AI</span>
          </button>
        </div>
      </nav>

      {/* /ai renders the same assistant full-page; everywhere else it floats over the module. */}
      {pathname.startsWith("/ai") ? null : <HubAiPanel mode="floating" />}

      {drawerOpen ? (
        <div className="drawer-scrim phone-only" onClick={() => setDrawerOpen(false)}>
          <aside className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-head">
              <AtwLogo size={36} onDark />
              <strong>ATW Hub</strong>
              <button type="button" className="icon-btn" aria-label="Close" onClick={() => setDrawerOpen(false)}>
                <X size={18} />
              </button>
            </div>
            {mobileModules.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.to}
                  type="button"
                  className="drawer-link"
                  onClick={() => {
                    setDrawerOpen(false);
                    if (item.to === "/ai") openHubAi();
                    else navigate(item.to);
                  }}
                >
                  <Icon size={18} /> {item.label}
                </button>
              );
            })}
            <button
              type="button"
              className="drawer-link"
              onClick={() => {
                setDrawerOpen(false);
                navigate("/settings");
              }}
            >
              <Settings size={18} /> Settings
            </button>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
